import { useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowLeftRight, Bot, CreditCard, FolderKanban, ScrollText, Settings, ShieldAlert, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

interface NavItem {
  nameKey: string;
  href: string;
  hash?: string;
  icon: JSX.Element;
  adminOnly?: boolean;
}

interface SidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, isMobileOpen, onCloseMobile }) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const currentHash = router.location.hash;

  useEffect(() => {
    onCloseMobile();
  }, [currentPath, onCloseMobile]);

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const navigation: NavItem[] = [
    { nameKey: 'nav.transmissionControl', href: '/transmission-control', icon: <ArrowLeftRight className="h-5 w-5" /> },
    { nameKey: 'nav.security', href: '/security', icon: <ShieldAlert className="h-5 w-5" /> },
    { nameKey: 'nav.logs', href: '/logs', icon: <ScrollText className="h-5 w-5" /> },
    { nameKey: 'nav.projects', href: '/projects', icon: <FolderKanban className="h-5 w-5" /> },
    { nameKey: 'nav.billing', href: '/billing', icon: <CreditCard className="h-5 w-5" /> },
    { nameKey: 'nav.users', href: '/users', icon: <Users className="h-5 w-5" />, adminOnly: true },
    { nameKey: 'nav.mcpAccess', href: '/settings', hash: 'hosted-mcp', icon: <Bot className="h-5 w-5" />, adminOnly: true },
    { nameKey: 'nav.settings', href: '/settings', icon: <Settings className="h-5 w-5" /> },
  ];
  const filteredNavigation = navigation.filter((item) => !item.adminOnly || isAdmin);

  return (
    <aside id="app-sidebar" className={`
      fixed inset-y-0 left-0 z-20 bg-card border-r pt-16
      transition-transform duration-300 ease-in-out
      ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      md:translate-x-0 md:block
      w-64 max-w-[85vw]
      ${isCollapsed ? 'md:w-20' : 'md:w-64'}
    `}>
      <div className="flex h-full flex-col">
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-6">
          {filteredNavigation.map((item) => {
            const isActive = currentPath === item.href
              && (item.hash ? currentHash === item.hash : !currentHash);
            return (
              <Link
                key={item.nameKey}
                to={item.href}
                hash={item.hash || ''}
                className={`
                  flex items-center ${isCollapsed ? 'px-2 md:justify-center' : 'px-4'} py-3 text-sm font-medium rounded-lg transition-colors
                  ${isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }
                `}
                title={isCollapsed ? t(item.nameKey) : ''}
              >
                <span className={isActive ? 'text-primary' : ''}>{item.icon}</span>
                {(!isCollapsed || isMobileOpen) && (
                  <span className={`ml-3 ${isCollapsed ? 'md:hidden' : ''}`}>{t(item.nameKey)}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {(!isCollapsed || isMobileOpen) ? (
          <div className={`border-t p-4 ${isCollapsed ? 'md:hidden' : ''}`}>
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary">
                <span className="text-sm font-medium text-primary-foreground">{user?.email?.[0].toUpperCase()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{user?.email}</p>
                <p className="text-xs capitalize text-muted-foreground">{user?.role}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden justify-center border-t p-4 md:flex">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
              <span className="text-sm font-medium text-primary-foreground">{user?.email?.[0].toUpperCase()}</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};
